import { Tabs, TabsContent, TabsList, TabsTrigger } from "@taiga-platform/ui";
import { Callout, Details, Mermaid } from "../components";

export function Product() {
  return (
    <>
      <h1>The Product</h1>

      <h2>The Problem with "Human in the Loop"</h2>
      <p>
        The industry's answer to AI governance is "put a human in the loop."
        That does not scale. Approving every AI action creates the same
        bottleneck enterprises were trying to eliminate. What enterprises
        actually need is governed autonomy — AI agents that act independently
        within clearly defined policy boundaries, with humans setting
        governance, not approving every action.
      </p>
      <p>
        TAIGA's core insight: governance should be structural, not
        procedural. Built into every agent action, not bolted on after.
      </p>

      <p>
        <strong>The core flow:</strong>
      </p>

      <p>
        Conversational spec -&gt; Structured backlog -&gt; Code generation
        -&gt; Governed deployment.
      </p>

      <p>
        A user describes what they need. TAIGA's AI agents structure it,
        plan it, build it, test it, deploy it — all governed by policy.
        Business intent in. Production software out. Fully governed.
      </p>

      <Callout variant="warning" title="What TAIGA is NOT">
        <ul>
          <li>
            Not a coding copilot (Cursor, Copilot, Claude Code — those are
            individual dev tools)
          </li>
          <li>
            Not an AI agent framework (LangChain, CrewAI — those are building
            blocks)
          </li>
          <li>
            Not runtime AI governance (Orq.ai, Deeploy — those govern after
            deployment)
          </li>
          <li>
            TAIGA governs the <em>development process itself</em> — upstream
            from all of the above
          </li>
        </ul>
      </Callout>

      <hr />

      <h2>The Governance Gap Nobody Sees</h2>
      <p>Here is something no security tool catches.</p>
      <p>
        A company ships AI-generated code that passes every security scanner.
        Functions perfectly. Contains zero CVEs. And is still in violation of
        its own policies. Still fails an audit. Still loses a deal because
        procurement flagged a compliance gap that no scanner was designed to
        detect.
      </p>
      <p>
        When a model generates code using an unapproved third-party library,
        that is not a security vulnerability — it is a procurement policy
        violation. When it stores personal data in a way that contradicts the
        data classification policy, that is not a CVE — it is a GDPR
        compliance failure. When it creates an API endpoint without the
        logging and audit trail that SOC 2 controls require, the code can be
        technically secure and functionally perfect and still make the
        organization non-compliant.
      </p>

      <h3>The quality crisis is measurable</h3>
      <p>This is not theoretical. The data is in.</p>

      <table>
        <thead>
          <tr>
            <th>Source</th>
            <th>Finding</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Veracode 2025</td>
            <td>
              AI-generated code has{" "}
              <strong>2.74x more vulnerabilities</strong> than human-written
              code. 45% failure rate on secure coding benchmarks.
            </td>
          </tr>
          <tr>
            <td>Apiiro Research</td>
            <td>
              <strong>322% more privilege escalation paths</strong>, 153%
              more architectural flaws, 40% more secrets exposure in
              AI-generated code.
            </td>
          </tr>
          <tr>
            <td>DryRun Security</td>
            <td>
              <strong>87% of AI agent-generated pull requests</strong>{" "}
              contain at least one security vulnerability.
            </td>
          </tr>
          <tr>
            <td>Perry et al. (Stanford)</td>
            <td>
              Developers using AI assistants produced{" "}
              <strong>less secure code</strong> while rating their code as{" "}
              <strong>more secure</strong>. The confidence gap is the
              danger.
            </td>
          </tr>
          <tr>
            <td>Georgetown CSET</td>
            <td>
              AI-generated code is vulnerable to{" "}
              <strong>data poisoning and backdoor attacks</strong> — a
              supply chain risk that traditional scanning cannot detect.
            </td>
          </tr>
        </tbody>
      </table>

      <p>
        Every one of these findings is a governance failure, not a security
        tool failure. The vulnerabilities exist because nobody enforced
        policy during generation — they only scanned after.
      </p>

      <Callout variant="danger" title="The Blind Spot">
        No linter checks whether code aligns with your SOC 2 control matrix.
        No scanner validates that data flows match your GDPR processing
        records. No SAST tool knows your internal architectural decision
        records. This is a governance problem, not a security problem. And
        the tooling for it essentially does not exist. Except in TAIGA.
      </Callout>

      <hr />

      <h2>Three Loops</h2>
      <p>
        TAIGA's product is organized around three reinforcing loops.
        Production feedback flows back to inform what to build next. AI
        agents collapse the handoffs between loops.
      </p>

      <table>
        <thead>
          <tr>
            <th>Loop</th>
            <th>Stages</th>
            <th>Core Question</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>LEARN</td>
            <td>Discovery -&gt; Specification -&gt; Validation</td>
            <td>Are we building the right thing?</td>
          </tr>
          <tr>
            <td>BUILD</td>
            <td>Planning -&gt; Building -&gt; Testing -&gt; Releasing</td>
            <td>Can we turn intent into production?</td>
          </tr>
          <tr>
            <td>RUN</td>
            <td>Running -&gt; Observing -&gt; Improving</td>
            <td>Is it working in the real world?</td>
          </tr>
        </tbody>
      </table>

      <Mermaid
        chart={`
          graph LR
              L[LEARN] -->|Validated Spec| B[BUILD]
              B -->|Deployed Software| R[RUN]
              R -->|Feedback| L
              G[Goals + Policies + Standards] -.->|Governs| L
              G -.->|Governs| B
              G -.->|Governs| R
        `}
      />

      <p>
        Business Goals, Policies, and Standards govern every loop. Not a
        layer on top — structural. Every AI agent operates within the
        boundaries set by organizational rules, compliance standards (ISO
        27001, SOC 2, GDPR, NIS2, DORA), and business objectives.
      </p>

      <hr />

      <h2>The Governance Framework Nobody Else Has</h2>

      <h3>Where it comes from</h3>
      <p>
        LFA — Logical Framework for Autonomous Action — is adapted from the
        Logical Framework Approach, developed by USAID in the 1960s for
        governing international development aid. It became the standard for
        the World Bank, EU, and most development agencies worldwide. The
        core idea: force separation between <em>what you want to achieve</em>{" "}
        and <em>what you plan to do</em>, then verify whether the goal was
        actually reached.
      </p>
      <p>
        The connection to software came through an unlikely path. Aki Hintsa
        was a Finnish physician and performance coach who worked with Formula
        1 drivers and world-class athletes. Before F1, he ran development aid
        projects in Ethiopia using LFA as standard practice. When he moved
        to elite sports coaching, he brought LFA with him — adapting it from
        governing aid budgets to governing human performance. Two matrices:
        an upper one for the strategic goal ("win the world championship")
        and a lower one for the execution program (training, nutrition,
        mental preparation).
      </p>

      <Callout variant="quote" title="Hintsa's principle">
        "Problem analysis first. Then goal. Then activities. Then
        measurement. This order never changes."
      </Callout>

      <p>
        TAIGA applies the same structure to software development. The domain
        changed, but the logic did not.
      </p>

      <h3>How it works: Two Matrices</h3>
      <p>
        The fundamental architectural decision in TAIGA is that there are{" "}
        <strong>two separate LFA matrices</strong>, not one. Each speaks a
        different language, serves a different audience, and operates at a
        different level of abstraction.
      </p>

      <div className="not-prose my-6">
        <Tabs defaultValue="upper">
          <TabsList>
            <TabsTrigger value="upper">Upper Matrix: Business LFA</TabsTrigger>
            <TabsTrigger value="lower">Lower Matrix: Software Dev LFA</TabsTrigger>
          </TabsList>
          <TabsContent value="upper" className="mt-4">
            <div className="prose [&]:max-w-none">
            <p>
              This is where leadership operates. It answers:{" "}
              <strong>"What business outcome are we trying to achieve?"</strong>
            </p>
            <table>
              <thead>
                <tr>
                  <th>Level</th>
                  <th>In TAIGA</th>
                  <th>Example</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td><strong>Goal</strong></td>
                  <td>The measurable business outcome</td>
                  <td>"Customer churn drops 15%"</td>
                </tr>
                <tr>
                  <td><strong>Purpose</strong></td>
                  <td>The behavioral change in users</td>
                  <td>"Users find answers themselves instead of calling support"</td>
                </tr>
                <tr>
                  <td><strong>Outputs</strong></td>
                  <td>The capabilities that enable the change</td>
                  <td>"Self-service knowledge base, contextual help system"</td>
                </tr>
                <tr>
                  <td><strong>Activities</strong></td>
                  <td>The projects that produce the outputs</td>
                  <td>"Build self-service experience" <em>(descends to lower matrix)</em></td>
                </tr>
              </tbody>
            </table>
            </div>
          </TabsContent>
          <TabsContent value="lower" className="mt-4">
            <div className="prose [&]:max-w-none">
            <p>
              This is where the engineering team and AI agents operate. It answers:{" "}
              <strong>"How do we build this correctly and safely?"</strong>
            </p>
            <table>
              <thead>
                <tr>
                  <th>Level</th>
                  <th>In TAIGA</th>
                  <th>Example</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td><strong>Goal</strong></td>
                  <td>Technical objective <em>(descended from upper)</em></td>
                  <td>"Self-service experience that fulfills the Purpose-level change"</td>
                </tr>
                <tr>
                  <td><strong>Purpose</strong></td>
                  <td>The software that enables the change</td>
                  <td>"Responsive knowledge base integrated with support systems"</td>
                </tr>
                <tr>
                  <td><strong>Outputs</strong></td>
                  <td>The technical artifacts</td>
                  <td>"Architecture, data flow, threat model, tested code, infrastructure"</td>
                </tr>
                <tr>
                  <td><strong>Activities</strong></td>
                  <td>Work done by agents and humans</td>
                  <td>"Specification, agent generation, testing, deployment"</td>
                </tr>
              </tbody>
            </table>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      <p>
        The two matrices connect at two coupling points. <strong>Down:</strong>{" "}
        the upper matrix's Activities become the lower matrix's Goal — this
        is how a business objective descends into a full software development
        lifecycle. <strong>Up:</strong> the lower matrix's operational data
        (monitoring, analytics, user feedback) rises to become evidence for
        whether the business goal was achieved.
      </p>

      <Callout variant="tip" title="Why Two Matrices Matter">
        <p>
          Consider: the software works perfectly (all tests pass, latency is
          low, uptime is 99.9%), but the business goal is not achieved
          (churn did not decrease). In a single-matrix model, the project
          would be marked as successful. In TAIGA's two-matrix model, the
          lower matrix shows success but the upper matrix shows failure —
          which is the correct representation of reality.
        </p>
        <p>
          <strong>
            This is the problem that no other tool in the market addresses.
          </strong>
        </p>
      </Callout>

      <h3>The vertical logic</h3>
      <p>
        LFA enforces an IF-THEN chain: IF Activities are carried out AND
        Assumptions hold -&gt; Outputs are produced -&gt; Purpose is achieved
        -&gt; Goal is reached. Every link in this chain must be explicit and
        testable. Every claim becomes measurable and auditable.
      </p>
      <p>
        When a policy is violated, the vertical logic breaks. The chain from
        business intent to deployed code has a gap. TAIGA's governance engine
        detects this structurally — not by scanning code after the fact, but
        by preventing the violation from entering the chain in the first
        place.
      </p>

      <Callout variant="quote" title="Competitive Differentiation">
        No competitor has an equivalent governance framework for AI agent
        orchestration. Most agent systems operate with implicit boundaries
        (prompt engineering) or no boundaries at all. TAIGA's LFA gives
        agents explicit, auditable autonomy boundaries — a requirement for
        enterprise adoption.
      </Callout>

      <hr />

      <h2>Enterprise Policy System</h2>
      <p>
        Every action in TAIGA is governed by enterprise policies. Seven
        policy types cover the full compliance surface:
      </p>

      <table>
        <thead>
          <tr>
            <th>Code</th>
            <th>Policy</th>
            <th>What It Governs</th>
            <th>Example</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>
              <strong>SEC</strong>
            </td>
            <td>Security &amp; Access</td>
            <td>
              Authentication, authorization, encryption, access controls
            </td>
            <td>"All API endpoints require JWT validation"</td>
          </tr>
          <tr>
            <td>
              <strong>DATA</strong>
            </td>
            <td>Data &amp; Privacy</td>
            <td>GDPR, data classification, retention, consent</td>
            <td>
              "Personal data retention max 2 years, right to erasure within
              30 days"
            </td>
          </tr>
          <tr>
            <td>
              <strong>SDLC</strong>
            </td>
            <td>Development Lifecycle</td>
            <td>Coding standards, review process, testing requirements</td>
            <td>"All code requires PR review before merge"</td>
          </tr>
          <tr>
            <td>
              <strong>OPS</strong>
            </td>
            <td>Operations &amp; Continuity</td>
            <td>
              Uptime, backups, disaster recovery, incident response
            </td>
            <td>"99.9% uptime SLA, daily backups, 4h RTO"</td>
          </tr>
          <tr>
            <td>
              <strong>TIC</strong>
            </td>
            <td>Third-Party &amp; AI</td>
            <td>Vendor assessment, API usage, AI model governance</td>
            <td>
              "Only approved LLM providers, no training on customer data"
            </td>
          </tr>
          <tr>
            <td>
              <strong>RSK</strong>
            </td>
            <td>Risk Management</td>
            <td>Risk assessment, mitigation strategies, escalation</td>
            <td>"Critical risks require board-level escalation"</td>
          </tr>
          <tr>
            <td>
              <strong>ARC</strong>
            </td>
            <td>Architecture Standards</td>
            <td>
              Technology choices, patterns, scalability, integration
            </td>
            <td>"EU-only data residency, PostgreSQL for primary data"</td>
          </tr>
        </tbody>
      </table>

      <p>
        Policies follow a draft/publish versioning model. Each tenant has
        their own set. Policies are immutable once published — a new version
        is created from a draft. The PolicyEngine evaluates policies
        hierarchically: Company -&gt; Team -&gt; Project, with more specific
        levels overriding general ones.
      </p>
      <p>
        In LFA terms, these policies are the <strong>Assumptions</strong> in
        the lower matrix: constraints that must hold true for the IF-THEN
        chain to be valid. If a DATA policy says "personal data retention
        max 2 years" and an AI agent generates a schema with no retention
        mechanism, the vertical logic breaks — and TAIGA catches it before
        the code exists.
      </p>

      <Details variant="info" title="How Policy Validation Works">
        <p>
          Spec field changes -&gt; Policy Engine loads effective policies for
          the tenant -&gt; evaluate against all active rules -&gt; return{" "}
          {"{allowed, violations, suggestions}"} -&gt; surface violations to
          user with alternatives.
        </p>
        <p>
          This happens before code generation, not after. The agent cannot
          proceed until the policy violation is resolved. This is structural
          enforcement — not a warning that can be dismissed.
        </p>
      </Details>

      <hr />

      <h2>The Wedge: Compliance Foundation Layer</h2>
      <p>
        The full TAIGA platform governs the entire software lifecycle. But
        customers do not need to deploy the full platform to get value on
        day one. The first wedge is the{" "}
        <strong>Compliance Foundation Layer</strong> — a standalone service
        that makes any existing AI development workflow compliance-aware,
        immediately.
      </p>
      <p>Here is how it works.</p>
      <p>
        A company's privacy policy, security policies, architectural
        standards, and compliance requirements are ingested into TAIGA's
        policy engine. The engine exposes these as an MCP server — a
        standardized interface that plugs into the developer tools teams are
        already using. Claude Code, Cursor, Copilot, any MCP-compatible
        tool. No migration. No new IDE. No retraining. The governance layer
        wraps around whatever the team already has.
      </p>
      <p>Then the interesting thing happens.</p>

      <Callout variant="note" title="Computer Says No">
        <p>
          A company's privacy policy states that they collect name, address,
          email, and phone number. Those are the PII categories they have
          declared, documented, and for which they have established legal
          basis under GDPR.
        </p>
        <p>
          Two weeks after onboarding, a developer is building a new customer
          registration page with Claude Code. Moving fast. They get an idea:
          "Maybe I should ask for the user's LinkedIn URL here — could be
          useful for the sales team."
        </p>
        <p>
          The MCP compliance check fires automatically. It cross-references
          the field against the company's declared data processing
          categories. LinkedIn URLs are social media profile data — a
          category the privacy policy does not cover. No legal basis
          established. No consent mechanism defined. No retention policy in
          place.
        </p>
        <p>
          The response is immediate:{" "}
          <em>
            "Your privacy policy does not include social media profiles as a
            declared data category. Adding this field would require updating
            your Record of Processing Activities, establishing legal basis,
            and defining retention. Do you want to proceed with a policy
            change request, or remove the field?"
          </em>
        </p>
        <p>
          The developer removes the field. The violation never reaches code
          review, never reaches production, never becomes a finding in the
          next audit. Compliance happened at the moment of creation — not as
          an afterthought.
        </p>
      </Callout>

      <p>
        This is not a theoretical example. This is the kind of policy
        violation that happens in every company, every week, and is only
        caught — if it is caught at all — months later during an audit or a
        customer security assessment.
      </p>

      <h3>Why start here</h3>
      <p>
        The Compliance Foundation Layer is the fastest path to value for
        three reasons:
      </p>
      <ol>
        <li>
          <strong>Zero disruption.</strong> Teams keep their existing tools.
          TAIGA plugs in as an MCP server. The integration is hours, not
          months.
        </li>
        <li>
          <strong>Immediate ROI.</strong> Every policy violation caught
          before code review saves hours of rework. Every compliance gap
          caught before an audit saves weeks. Every GDPR issue caught before
          a breach saves the organization.
        </li>
        <li>
          <strong>The gateway to everything else.</strong> Once the policy
          engine is running and developers experience real-time compliance
          checking, the path to full TAIGA adoption becomes natural:
          compliance-aware specifications, governed code generation,
          auditable deployments, and the full Learn-Build-Run loop.
        </li>
      </ol>

      <Mermaid
        chart={`
          graph LR
              A[Compliance<br/>Foundation Layer] -->|Policies proven| B[Compliance-Aware<br/>Spec & Learn Loop]
              B -->|Specs governed| C[Governed Coding<br/>Agents]
              C -->|Code governed| D[Governed Deployments<br/>& Production]
              D -->|Ops feedback| E[Full Learn-Build-Run<br/>Loop]
        `}
      />

      <p>
        The wedge is small enough to close in days. The expansion path leads
        to the full platform. And every step compounds the governance
        library that becomes the moat.
      </p>

      <hr />

      <h2>The Data Flywheel</h2>
      <p>
        Every project TAIGA delivers generates reusable assets: Policy
        Cards, design patterns, validated workflows, compliance mappings,
        and tested architectural decisions. These compound.
      </p>
      <p>
        The first project for a healthcare customer requires building GDPR
        compliance patterns from scratch. The second healthcare project
        reuses them. By the tenth, the governance library handles most
        compliance requirements automatically. The more projects TAIGA
        delivers, the faster and cheaper each subsequent delivery becomes —
        while quality increases.
      </p>

      <Callout variant="tip" title="Compounding Assets">
        Policy Cards are machine-readable governance rules embedded in the
        platform. They define what an AI agent is allowed to do, under what
        conditions, and with what approvals. Each engagement grows the
        library. This is the moat — accumulated governance knowledge from
        every customer, every industry, every compliance standard. It
        cannot be replicated by a team that has not done the work.
      </Callout>

      <p>
        The "Wedge Strategy" makes this self-funding: TAIGA gets paid to
        build its own product. Customer projects fund R&amp;D and build the
        governance library for the platform phase.
      </p>

      <hr />

      <h2>Discovery Pipeline</h2>
      <p>
        When a user starts a new project, TAIGA generates eight artifacts in
        dependency order:
      </p>

      <Mermaid
        chart={`
          graph TD
              S[Specification] --> UF[User Flows]
              UF --> A[Architecture]
              A --> TD[Tech Decisions]
              A --> DF[Data Flow]
              DF --> TM[Threat Model]
              DF --> DPIA[DPIA]
              TM --> RR[Risk Register]
              DPIA --> RR
        `}
      />

      <p>
        Each artifact has states: <strong>locked</strong> (dependencies not
        met), <strong>available</strong> (ready to generate),{" "}
        <strong>generating</strong> (agent working), <strong>complete</strong>.
        The pipeline service computes these states and determines the
        current phase and next recommended action.
      </p>
      <p>
        All eight artifacts are generated by AI agents, validated against
        enterprise policies, and traceable to the specification. Traditional
        consulting produces the same artifacts manually over 2-4 weeks.
        TAIGA generates them in hours.
      </p>

      <Callout variant="success" title="What Gets Generated Per Project">
        Project Specification, User Flows, Architecture Overview, Technology
        Decisions, Data Flow Diagram, Threat Model, DPIA, Risk Register —
        plus a full Backlog with task breakdown and estimates. All
        compliant, all living documents.
      </Callout>

      <hr />

      <h2>What's Built Today</h2>

      <h3>Six AI Agents</h3>
      <table>
        <thead>
          <tr>
            <th>Agent</th>
            <th>Loop</th>
            <th>Purpose</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>spec-chat</td>
            <td>Learn</td>
            <td>Conversational requirements gathering</td>
            <td>Built</td>
          </tr>
          <tr>
            <td>ux-planning</td>
            <td>Learn</td>
            <td>UI/UX specification and wireframing</td>
            <td>Built</td>
          </tr>
          <tr>
            <td>technical-design</td>
            <td>Learn</td>
            <td>Architecture and technical specification</td>
            <td>Built</td>
          </tr>
          <tr>
            <td>security-risk</td>
            <td>Learn</td>
            <td>Security assessment and risk analysis</td>
            <td>Built</td>
          </tr>
          <tr>
            <td>planning</td>
            <td>Build</td>
            <td>Backlog creation, task breakdown, estimation</td>
            <td>Built</td>
          </tr>
          <tr>
            <td>coding</td>
            <td>Build</td>
            <td>Code generation, testing, deployment</td>
            <td>Built</td>
          </tr>
        </tbody>
      </table>

      <h3>LLM Council Pattern</h3>
      <p>
        For high-stakes generation (PRDs, backlogs), TAIGA uses a
        multi-model council: multiple LLM models generate independently,
        cross-review each other's output for completeness and policy
        compliance, then a primary model synthesizes the final result. This
        catches blind spots that any single model would miss. Reserved for
        high-stakes generation where quality matters most — not used for
        simple chat.
      </p>

      <h3>Coding Agent Eval Harness</h3>
      <p>
        The coding agent includes a built-in evaluation harness with five
        difficulty tiers. This enables data-driven agent development — not
        guesswork.
      </p>

      <table>
        <thead>
          <tr>
            <th>Tier</th>
            <th>Description</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Trivial</td>
            <td>Simple function generation, boilerplate code</td>
          </tr>
          <tr>
            <td>Easy</td>
            <td>Standard CRUD, basic patterns</td>
          </tr>
          <tr>
            <td>Medium</td>
            <td>Multi-file changes, moderate complexity</td>
          </tr>
          <tr>
            <td>Hard</td>
            <td>Complex integrations, architectural changes</td>
          </tr>
          <tr>
            <td>Expert</td>
            <td>Full-stack features, cross-cutting concerns</td>
          </tr>
        </tbody>
      </table>

      <Details variant="info" title="Key Data Flows">
        <p>
          <strong>Chat to Specification.</strong> User message -&gt;
          AgentCore Gateway (JWT validation) -&gt; Spec Chat Agent -&gt; load
          session memory + current spec/policies via Main API -&gt; Bedrock
          (Claude) with context -&gt; stream response -&gt; if spec change
          proposed: Main API validates against policies -&gt; update spec +
          audit log -&gt; update session memory.
        </p>
        <p>
          <strong>Backlog Generation (LLM Council).</strong> User triggers
          -&gt; AgentCore Gateway -&gt; Backlog Council Agent -&gt; load
          finalized spec + policies -&gt; Stage 1: multiple models generate
          independently -&gt; Stage 2: cross-model review for completeness
          and policy compliance -&gt; Stage 3: synthesize final backlog
          -&gt; store via Main API with traceability -&gt; stream progress.
        </p>
      </Details>

      <hr />

      <h2>Architecture</h2>

      <h3>Tech Stack</h3>
      <table>
        <thead>
          <tr>
            <th>Layer</th>
            <th>Technology</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Frontend</td>
            <td>React 19, TypeScript, Vite, shadcn/ui, Tailwind CSS</td>
          </tr>
          <tr>
            <td>Backend</td>
            <td>Fastify 5 + TypeScript on ECS Fargate</td>
          </tr>
          <tr>
            <td>AI</td>
            <td>
              Anthropic Claude (primary) + OpenAI GPT-4o (council) via
              Bedrock
            </td>
          </tr>
          <tr>
            <td>Data</td>
            <td>PostgreSQL RDS with Row-Level Security, S3 Artifacts</td>
          </tr>
          <tr>
            <td>Auth</td>
            <td>AWS Cognito (Google OAuth, OIDC-ready for Entra ID)</td>
          </tr>
          <tr>
            <td>IaC</td>
            <td>Terraform (state in S3 + DynamoDB)</td>
          </tr>
        </tbody>
      </table>

      <p>
        <strong>Region:</strong> AWS eu-central-1 (Frankfurt). EU data
        residency.
      </p>

      <h3>Dual API</h3>
      <p>
        Two distinct API layers: Main API (Fastify) handles data CRUD as a
        conventional web API. AgentCore Gateway (AWS Bedrock AgentCore)
        handles AI agent execution in isolated microVMs. Neither can
        interfere with the other.
      </p>

      <h3>Three-Layer Auth</h3>

      <Mermaid
        chart={`
          graph TB
              R[Request] --> AG[API Gateway<br/>Token validation + rate limiting + WAF]
              AG --> RBA[Role-Based Access<br/>Business logic authorization]
              RBA --> RLS[PostgreSQL RLS<br/>Database-level tenant isolation]
        `}
      />

      <p>
        A compromised application layer cannot access another tenant's data
        because RLS enforces isolation independently.
      </p>

      <h3>Key Design Decisions</h3>
      <ul>
        <li>
          <strong>Multi-tenant RLS isolation.</strong> Single shared
          database, Row-Level Security on all tenant-owned tables. Every
          table carries a <code>tenant_id</code> column. Tenant context set
          per transaction.
        </li>
        <li>
          <strong>Immutable audit events.</strong> Append-only table, no
          UPDATE or DELETE permissions. Every mutation emits an event with
          actor, action, target, changes, and correlation ID.
        </li>
        <li>
          <strong>Hierarchical policy system.</strong> Company -&gt; Team
          -&gt; Project. Central PolicyEngine evaluates before spec
          changes. Designed for future migration to Cedar or OPA.
        </li>
      </ul>

      <hr />

      <h2>Autonomy Levels</h2>
      <p>
        Not all decisions are equal. TAIGA defines four autonomy levels that
        determine when agents act independently and when they defer to
        humans. The customer configures these per project — a bank might
        require Explicit Approval for everything, while a startup might run
        most tasks at Silent Approval.
      </p>

      <table>
        <thead>
          <tr>
            <th>Level</th>
            <th>Description</th>
            <th>Example</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Fully Autonomous</td>
            <td>
              Routine, bounded, reversible changes proceed without human
              approval
            </td>
            <td>Formatting, dependency updates, test generation</td>
          </tr>
          <tr>
            <td>Silent Approval</td>
            <td>
              Changes proceed automatically but are visible and interruptible
            </td>
            <td>Code refactoring, documentation updates</td>
          </tr>
          <tr>
            <td>Explicit Approval</td>
            <td>
              New features, user-facing changes, integrations require human
              decision
            </td>
            <td>API changes, database migrations, new endpoints</td>
          </tr>
          <tr>
            <td>Decision Support</td>
            <td>
              Contradictory signals or multiple options — system provides
              justified overview
            </td>
            <td>Architecture choices, technology selection</td>
          </tr>
        </tbody>
      </table>

      <Callout variant="info" title="Configurable Governance">
        This is not a fixed hierarchy. It is a configurable governance model
        that adapts to each organization's risk tolerance. The LFA framework
        ensures agents cannot exceed their defined autonomy level —
        structurally, not by convention.
      </Callout>

      <hr />

      <h2>What's Next</h2>
      <table>
        <thead>
          <tr>
            <th>Phase</th>
            <th>Timeline</th>
            <th>Key Deliverables</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Phase 1: Customer Focus</td>
            <td>M1-M12</td>
            <td>
              Core LFA-to-Code loop, pilots, Policy Card library, first key
              hires
            </td>
          </tr>
          <tr>
            <td>Phase 2: Customer Independence</td>
            <td>M12-M24</td>
            <td>
              Self-service portal, multi-tenant, hosted platform, Business
              Logframe UI
            </td>
          </tr>
          <tr>
            <td>Phase 3: Global Scale</td>
            <td>M24+</td>
            <td>
              Full self-service SaaS, Policy Card marketplace, Operations
              Loop, international expansion
            </td>
          </tr>
        </tbody>
      </table>

      <Details variant="info" title="Phase 2: Business Logframe Implementation">
        The upper matrix becomes a first-class data structure:{" "}
        <code>business_logframes</code> table with goal, purpose,
        assumptions, indicators, verification (all JSONB). Same draft/publish
        versioning model, RLS with tenant isolation. New Business Objective
        view in project main view, before the three loops. Spec wizard
        sections gain explicit references: Goals derived from Business Goal,
        Success Metrics derived from Business Indicators.
      </Details>

      <Details variant="info" title="Phase 3: Feedback Coupling">
        The Operations Loop connects to the Business Logframe. Infrastructure
        monitoring (CloudWatch, Datadog), user analytics, and feedback
        collection (NPS, support tickets) are compared against Business
        Indicators. Goal Verification dashboard shows: achieved, partially
        achieved, not achieved, measurement in progress. If the goal is not
        being achieved, the system triggers a return to the Learning Loop —
        closing the full cycle from business intent to production evidence.
      </Details>
    </>
  );
}
